const API_BASE = '/api'

export async function fetchJson(path, options = {}) {
  const url = `${API_BASE}${path}`
  try {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options,
    })
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`${options.method || 'GET'} ${url} -> ${res.status} ${text || res.statusText}`)
    }
    return res.json()
  } catch (err) {
    throw new Error(`${options.method || 'GET'} ${url} failed: ${err.message || String(err)}`)
  }
}

export const api = {
  health: () => fetch('/health').then(res => res.json()),
  dashboard: () => fetchJson('/dashboard'),
  categories: () => fetchJson('/categories'),
  vtneDomains: () => fetchJson('/vtne-domains'),
  importRuns: () => fetchJson('/imports'),
  uploadGift: async (file) => {
    const form = new FormData()
    form.append('file', file)
    const url = `${API_BASE}/imports/gift`
    try {
      const res = await fetch(url, { method: 'POST', body: form })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(`POST ${url} -> ${res.status} ${text || res.statusText}`)
      }
      return res.json()
    } catch (err) {
      throw new Error(`POST ${url} failed: ${err.message || String(err)}`)
    }
  },
  parseImport: (id) => fetchJson(`/imports/${id}/parse`, { method: 'POST' }),
  importItems: (id, params = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
    })
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return fetchJson(`/imports/${id}/items${suffix}`)
  },
  importItem: (id) => fetchJson(`/import-items/${id}`),
  updateImportItem: (id, payload) => fetchJson(`/import-items/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  escalateImportItemToExpert: (id) => fetchJson(`/import-items/${id}/escalate-to-expert`, { method: 'PATCH' }),
  bulkImportItems: (importRunId, payload) => fetchJson(`/imports/${importRunId}/items/bulk-action`, { method: 'POST', body: JSON.stringify(payload) }),
  bulkExportReviewPacket: (payload) => fetchJson('/import-items/bulk-export-review-packet', { method: 'POST', body: JSON.stringify(payload) }),
  approveAllReadyImportItems: (importRunId, payload) => fetchJson(`/imports/${importRunId}/approve-all-ready`, { method: 'POST', body: JSON.stringify(payload) }),
  duplicateSuggestions: (id) => fetchJson(`/import-items/${id}/duplicate-suggestions`),
  compareImportItem: (importItemId, questionId) => fetchJson(`/import-items/${importItemId}/compare/${questionId}`),
  approveImportItem: (id) => fetchJson(`/import-items/${id}/approve`, { method: 'POST' }),
  rejectImportItem: (id) => fetchJson(`/import-items/${id}/reject`, { method: 'POST' }),
  questions: (params = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
    })
    return fetchJson(`/questions?${qs.toString()}`)
  },
  question: (id) => fetchJson(`/questions/${id}`),
  createQuestion: (payload) => fetchJson(`/questions`, { method: 'POST', body: JSON.stringify(payload) }),
  updateQuestion: (id, payload) => fetchJson(`/questions/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  bulkQuestions: (payload) => fetchJson(`/questions/bulk-action`, { method: 'POST', body: JSON.stringify(payload) }),
  qaCheckQuestion: (id) => fetchJson(`/questions/${id}/qa-check`),
  qaCheckBatch: (questionIds) => fetchJson(`/questions/qa-check-batch`, { method: 'POST', body: JSON.stringify({ question_ids: questionIds }) }),
  quizzes: () => fetchJson('/quizzes'),
  quiz: (id) => fetchJson(`/quizzes/${id}`),
  createQuiz: (payload) => fetchJson(`/quizzes`, { method: 'POST', body: JSON.stringify(payload) }),
  updateQuiz: (id, payload) => fetchJson(`/quizzes/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteQuiz: (id) => fetchJson(`/quizzes/${id}`, { method: 'DELETE' }),
  quizItems: (id) => fetchJson(`/quizzes/${id}/items`),
  addQuizItem: (quizId, payload) => fetchJson(`/quizzes/${quizId}/items`, { method: 'POST', body: JSON.stringify(payload) }),
  updateQuizItem: (quizId, itemId, payload) => fetchJson(`/quizzes/${quizId}/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteQuizItem: (quizId, itemId) => fetchJson(`/quizzes/${quizId}/items/${itemId}`, { method: 'DELETE' }),
  exportQuizGift: (quizId) => fetchJson(`/quizzes/${quizId}/export/gift`, { method: 'POST' }),
  exportQuizAnswerKey: (quizId) => fetchJson(`/quizzes/${quizId}/export/answer-key-pdf`, { method: 'POST' }),
  exportQuizStudentHandout: (quizId) => fetchJson(`/quizzes/${quizId}/export/student-handout-pdf`, { method: 'POST' }),
  templates: () => fetchJson('/quiz-templates'),
  createTemplate: (quizId, payload) => fetchJson(`/quizzes/${quizId}/templates`, { method: 'POST', body: JSON.stringify(payload) }),
  getTemplate: (templateId) => fetchJson(`/quiz-templates/${templateId}`),
  updateTemplate: (templateId, payload) => fetchJson(`/quiz-templates/${templateId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteTemplate: (templateId) => fetchJson(`/quiz-templates/${templateId}`, { method: 'DELETE' }),
  restoreTemplate: (templateId, newTitle) => fetchJson(`/quiz-templates/${templateId}/restore`, { method: 'POST', body: JSON.stringify({ new_title: newTitle }) }),
  createRuleBasedTemplate: (payload) => fetchJson(`/quiz-templates/rule-based`, { method: 'POST', body: JSON.stringify(payload) }),
  getQuizSnapshots: (quizId) => fetchJson(`/quizzes/${quizId}/snapshots`),
}
